import Head from 'next/head';
import type { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../_app';
import React from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Layout from '@/layouts';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { fetchEmailFinderAIPrompts, fetchLoadingDataWith } from '@/lib/service';
import { page3WithWebApplicationJsonLd } from '@/lib/jsonld';
import { pageTypeEnum } from '@/components/email-finder/page-client';

const PageMainContent = dynamic(() => import('@/components/email-finder/page-client'), {
  ssr: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl } = context;
  // const path = resolvedUrl.split('?')[0];
  const path = '/instagram-email-finder';

  const [localeRes, tdkRes, dataRes, emailFinderAIPromptsRes] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(path, locale),
    fetchLoadingDataWith(path, locale),
    fetchEmailFinderAIPrompts(),
  ]);

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;

  const jsonLd = page3WithWebApplicationJsonLd(
    {
      title: tdkRes?.data.title || '',
      desc: tdkRes?.data?.description ?? tempData?.head_Meta_Desc ?? null,
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      title: 'Toolkit',
      url: siteUrl + '/toolkit',
    },
    {
      url: siteUrl,
    },
    {
      name: 'Instagram Email Finder',
      url: 'https://www.dolphinradar.com/instagram-email-finder',
      applicationCategory: 'Instagram Email finder',
      description:
        'Find and scrape public Instagram emails from hashtag, keyword, location, profile URL, followers, and followings — build verified outreach lists safely.',
      featureList: [
        'scrape emails by hashtag',
        'Scrape Emails from Keyword',
        'instagram email extractor',
        'Scrape Emails from Follower Lists',
        'Scrape Emails From Profile URL',
      ],
    }
  );

  if (!dataRes?.data) {
    return {
      redirect: {
        destination: `/`,
        permanent: false,
      },
    };
  }
  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      locale,
      title: tdkRes?.data?.title ?? tempData?.head_Anonymous_Meta_Title ?? null,
      description: tdkRes?.data?.description ?? tempData?.head_Anonymously_Meta_Desc ?? null,
      contentData: dataRes?.data ?? null,
      jsonLd,
      emailFinderAIPrompts: emailFinderAIPromptsRes?.data || [],
    },
  };
};

const PageContent: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  return <PageMainContent key="all" data={props} type={pageTypeEnum.syntheticPage} />;
};

PageContent.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PageContent;
